<template>
  <section class="container-full-5">
    <div class="d-flex align-items-center gap-4 mb-6">
      <button class="btn white2" @click="goBack">
        <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
        <span>Retour</span>
      </button>
      <h2 class="secondary-font gap-4 mt-2 mb-0">Membres - Détail du membre</h2>
    </div>

    <div class="clear-both h-0"></div>

    <div v-if="loading" class="flex-center py-10">
      <div class="loader dark"></div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger mb-4">
      {{ errorMessage }}
    </div>

    <div v-if="member" class="s20 widget pt-8 pb-8">
      <div class="flex px-6">
        <div class="s3_mobile px-4">
          <div class="rounded-100 overflow-hidden item active">
            <img :src="member.photo_url ? member.photo_url : '/public/assets/users/300w/user2.jpg'" width="100%" />
          </div>
        </div>

        <div class="s1_mobile flex-center">&nbsp;</div>
        
        <div class="s4_mobile">
          <h3 class="my-2">{{ member.prenom || '' }}</h3>
          <h6 class="font-normal my-2">{{ member.prenom }} {{ member.nom }}</h6>
          <h6 class="text-primary-color my-4">{{ member.collectivite?.nom || 'N/A' }}</h6>
          <p class="flex gap-2 text-grey-600 my-3">
            <img src="/public/icons/icon-time-glossy.svg" class="size-xs" alt="" />
            Dernière connexion: {{ formatDate(member.last_connection_at || member.created_at) }}
          </p>
          <span class="tag-state notif stroke">{{ member.actif === 1 ? 'Actif' : 'Inactif' }}</span>
        </div>

        <div class="s1_mobile flex-center">
          <hr class="hr-vertical h-90p"/>
        </div>

        <div class="s9_mobile px-8">
          <h5 class="text-grey-600 my-2">1280 Actions</h5>
          <h5 class="font-normal text-grey-600 my-2">1280 Actions</h5>
          <h5 class="font-normal text-grey-600 my-2">1280 Actions</h5>
        </div>

        <div class="s3_mobile px-2">
          <div class="flex flex-col gap-4">
            <button class="btn" @click="handleEdit" :disabled="actionLoading">
              <img src="/public/icons/icon-edit.svg" class="btn-icon" alt="" />
              <span>Editer</span>
            </button>
            <button 
              class="btn-ultra color2" 
              :class="{ 'display-none': member.deleted_at !== null || member.actif !== 1 }"
              :disabled="actionLoading"
              @click="doToggleEtatValideAction"
            >
              <div class="btn-show">
                <img src="/public/icons/icon-actif.svg" class="btn-icon" alt="" />
                <span>Actif</span>
              </div>
              <div class="btn-loading" :class="{ 'display-none': !actionLoading }">
                <div class="loader-dots"></div>
              </div>
            </button>
            <button 
              class="btn-ultra white2 tips" 
              :class="{ 'display-none': member.deleted_at === null }"
              :disabled="actionLoading"
              @click="doDeleteItem"
            >
              <div class="btn-show">
                <img src="/public/icons/icon-destroy.svg" class="btn-icon icon-neutral" alt="" />
                <span>Détruire</span>
              </div>
              <div class="btn-loading" :class="{ 'display-none': !actionLoading }">
                <div class="loader-dots"></div>
              </div>
            </button>
            <button 
              class="btn-ultra white2" 
              :class="{ 'display-none': member.deleted_at !== null }"
              :disabled="actionLoading"
              @click="doDeleteItem"
            >
              <div class="btn-show">
                <img src="/public/icons/icon-delete.svg" class="btn-icon" alt="" />
                <span>Supprimer</span>
              </div>
              <div class="btn-loading" :class="{ 'display-none': !actionLoading }">
                <div class="loader-dots"></div>
              </div>
            </button>
            <button 
              class="btn-ultra white2" 
              :class="{ 'display-none': member.deleted_at === null }"
              :disabled="actionLoading"
              @click="doRestoreItem"
            >
              <div class="btn-show">
                <img src="/public/icons/icon-restaurer.svg" class="btn-icon icon-neutral" alt="" />
                <span>Restaurer</span>
              </div>
              <div class="btn-loading" :class="{ 'display-none': !actionLoading }">
                <div class="loader-dots"></div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div class="clear-both h-10"></div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import membersService from '@/services/membersService'
import { showAlert, showConfirm } from '@/utils/swal'

interface Member {
  id: number
  nom: string
  prenom: string
  collectivite?: {
    id: number
    nom: string
  }
  photo?: string
  photo_url?: string
  email?: string
  telephone?: string
  created_at?: string
  last_connection_at?: string
  actif?: number
  deleted_at?: string | null
}

const route = useRoute()
const router = useRouter()
const member = ref<Member | null>(null)
const loading = ref(false)
const actionLoading = ref(false)
const errorMessage = ref('')

onMounted(async () => {
  const memberId = route.params.id
  if (memberId) {
    await loadMember(Number(memberId))
  } else {
    router.push({ name: 'collectivites-membres' })
  }
})

async function loadMember(id: number) {
  try {
    loading.value = true
    errorMessage.value = ''
    const data = await membersService.getMember(id)
    member.value = data
  } catch (error: any) {
    errorMessage.value = error.message || 'Erreur lors du chargement du membre'
    // Rediriger vers la liste si le membre n'est pas trouvé
    setTimeout(() => {
      router.push({ name: 'collectivites-membres' })
    }, 2000)
  } finally {
    loading.value = false
  }
}

function formatDate(dateString?: string): string {
  if (!dateString) return '13/12/2025'
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function goBack() {
  router.push({ name: 'collectivites-membres' })
}

async function doDeleteItem() {
  if (!member.value) return

  const confirmation = await showConfirm(
    `Voulez-vous vraiment supprimer le membre ${member.value.nom} ${member.value.prenom} ?`,
    'Confirmation de suppression'
  )
  if (!confirmation) return

  try {
    actionLoading.value = true
    errorMessage.value = ''
    await membersService.deleteMember(member.value.id)
    await showAlert.success('Membre supprimé avec succès.', 'Suppression réussie')
    // Rediriger vers la liste après suppression
    router.push({ name: 'collectivites-membres' })
  } catch (error: any) {
    errorMessage.value = error.message || 'Erreur lors de la suppression du membre.'
    await showAlert.error(errorMessage.value, 'Erreur de suppression')
  } finally {
    actionLoading.value = false
  }
}

async function doRestoreItem() {
  if (!member.value) return

  const confirmation = await showConfirm(
    `Voulez-vous vraiment restaurer le membre ${member.value.nom} ${member.value.prenom} ?`,
    'Confirmation de restauration'
  )
  if (!confirmation) return

  try {
    actionLoading.value = true
    errorMessage.value = ''
    await membersService.restoreMember(member.value.id)
    await showAlert.success('Membre restauré avec succès.', 'Restauration réussie')
    // Recharger les données du membre
    await loadMember(member.value.id)
  } catch (error: any) {
    errorMessage.value = error.message || 'Erreur lors de la restauration du membre.'
    await showAlert.error(errorMessage.value, 'Erreur de restauration')
  } finally {
    actionLoading.value = false
  }
}

async function doToggleEtatValideAction() {
  if (!member.value) return

  const confirmation = await showConfirm(
    `Voulez-vous vraiment modifier l'état du membre ${member.value.nom} ${member.value.prenom} ?`,
    'Confirmation de modification'
  )
  if (!confirmation) return

  try {
    actionLoading.value = true
    errorMessage.value = ''
    await membersService.toggleEtatValide([member.value.id])
    await showAlert.success('État du membre mis à jour avec succès.', 'Mise à jour réussie')
    // Recharger les données du membre
    await loadMember(member.value.id)
  } catch (error: any) {
    errorMessage.value = error.message || 'Erreur lors de la modification de l\'état du membre.'
    await showAlert.error(errorMessage.value, 'Erreur de mise à jour')
  } finally {
    actionLoading.value = false
  }
}

function handleEdit() {
  if (member.value) {
    router.push({ name: 'collectivites-membre-edit', params: { id: member.value.id } })
  }
}
</script>

<style scoped>
</style>

